/**
 * Coque UNIQUE des pages hors de l'application interne : portail client,
 * pages publiques à lien, espace sous-traitant, portail fournisseur.
 *
 * Pas de navigation métier : un cadre léger à la marque de l'ENTREPRISE
 * prestataire (Q102) dès que le lien ou la session la fait connaître, celle de
 * MyEquip avant. Emplacements : marque (lien vers l'accueil de l'espace),
 * identité de la personne connectée, téléphone de l'entreprise (« Appeler »),
 * déconnexion, liens de pied (données personnelles, mentions). Le titre et le
 * retour de la page viennent de son `PageHeader`.
 *
 * Une fois l'entreprise connue, la marque de la plateforme n'apparaît plus.
 * Classes stables pour les tests : `.portal-shell`, `.portal-topbar`.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { LogOut, Phone } from 'lucide-react';
import type { ShellBranding, TenantBranding } from '@myequip/shared';
import { t } from '../../core/i18n.js';
import { brandName } from '../../core/branding.js';
import { Logo, LogoMark } from '../Logo.js';
import { CallButton } from '../CallButton.js';
import { buttonClass } from '../ui/action.js';

export interface ExternalShellLink {
  label: string;
  /** Page de l'application (`/rgpd?…`) ou adresse externe (`https://…`). */
  to: string;
}

export function ExternalShell(props: {
  /**
   * Marque de l'entreprise, connue une fois le lien ou la session vérifiés.
   * Absente : marque de la plateforme (avant connexion, lien invalide).
   */
  branding?: ShellBranding | TenantBranding | null | undefined;
  /** Nom de l'entreprise, repli quand le branding n'a pas de nom d'affichage. */
  companyName?: string | undefined;
  /**
   * Logo servi par une route liée au jeton de la page (suivi d'intervention),
   * quand l'identifiant du fichier du logo ne doit pas être exposé.
   */
  logoSrc?: string | undefined;
  /** Qui est connecté (nom du client, du sous-traitant…). */
  identity?: string | undefined;
  /** Téléphone de l'entreprise : bouton « Appeler » dans la barre. */
  phone?: string | null | undefined;
  /** Accueil de l'espace : la marque y ramène. Sans accueil, elle n'est pas un lien. */
  home?: string | undefined;
  onLogout?: (() => void) | undefined;
  /** Contenu en deux colonnes sur ordinateur (accueil du portail) : la page s'élargit dès 900 px. */
  wide?: boolean | undefined;
  /** Liens du pied de page (données personnelles, mentions légales). */
  links?: readonly ExternalShellLink[] | undefined;
  children: ReactNode;
}): ReactNode {
  const tenant = Boolean(props.branding || props.companyName || props.logoSrc);
  const name = props.branding?.displayName ?? props.companyName ?? brandName(props.branding);
  // Sans entreprise : le logo MyEquip officiel, jamais le symbole suivi du nom retapé.
  const mark = tenant ? (
    <>
      {props.logoSrc
        ? <img className="ext-shell__logo" src={props.logoSrc} alt="" width={32} height={32} />
        : <LogoMark size={32} branding={props.branding} name={name} />}
      <span className="ext-shell__name">{name}</span>
    </>
  ) : <Logo size={26} />;
  return (
    <div className={`portal-shell ext-shell${props.wide ? ' ext-shell--wide' : ''}`}>
      <header className="portal-topbar ext-shell__topbar">
        {props.home ? (
          // Nom accessible tiré du contenu (logo ou nom affiché), sans libellé qui le
          // contredirait : les initiales du monogramme comptent comme texte visible.
          <Link to={props.home} className="ext-shell__brand">{mark}</Link>
        ) : (
          <span className="ext-shell__brand">{mark}</span>
        )}
        {props.identity ? <span className="ext-shell__identity">{props.identity}</span> : null}
        {props.phone ? (
          <CallButton
            phone={props.phone}
            who={tenant ? name : undefined}
            variant="ghost"
            ariaLabel={tenant ? t('externe.appelerEntreprise', { entreprise: name }) : t('externe.appeler')}
          >
            <Phone size={18} aria-hidden="true" />
            <span className="ext-shell__call">{t('externe.appeler')}</span>
          </CallButton>
        ) : null}
        {props.onLogout ? (
          <button
            type="button"
            className={buttonClass('ghost', false, false)}
            onClick={props.onLogout}
            aria-label={t('auth.deconnexion')}
          >
            <LogOut size={18} aria-hidden="true" />
            <span className="ext-shell__label">{t('auth.deconnexion')}</span>
          </button>
        ) : null}
      </header>
      <main className="ext-shell__main anim-page">{props.children}</main>
      <footer className="ext-shell__footer">
        <span>{name}</span>
        {/* Espace d'une entreprise : MyEquip en simple signature, après sa marque. */}
        {tenant ? (
          <span className="ext-shell__powered">
            {t('externe.propulsePar')}
            <Logo size={14} />
          </span>
        ) : null}
        {props.links?.length ? (
          <nav className="ext-shell__links" aria-label={t('externe.vosDonnees')}>
            {props.links.map((link) => (/^https?:\/\//.test(link.to)
              ? <a key={link.to} href={link.to} target="_blank" rel="noreferrer">{link.label}</a>
              : <Link key={link.to} to={link.to}>{link.label}</Link>))}
          </nav>
        ) : null}
      </footer>
    </div>
  );
}
